import React, { Fragment } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckBoxOutlineBlank from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import moment from "moment-timezone";
import formatAircraftSelection from "../../helpers/format-aircraft-selection";
import formatStage from "../../helpers/lead-stage-formatter";
import currencyFormatter from "../../helpers/currencyFormatter";
import { formatTripDate } from "../../helpers/dates";
import timeFormatter from "../../helpers/timeFormatter";
import getTripOptionOperatorPrice from "../../helpers/get-trip-option-operator-price";
import { Link } from "react-router-dom";
import {Button} from "@mobiscroll/react";

const getAircraftSelection = (data) => {
  if (data?.aircraftSelection?.length) {
    return data?.aircraftSelection
      .map((aircraft) => formatAircraftSelection(aircraft))
      .join(", ");
  }
  return "N/A";
};

function Row(props) {
  const { row, superAdminOrAd } = props;
  const [open, setOpen] = React.useState(false);

  // Check if it's a multileg trip
  const isMultileg = row.tripType === 3;

  return (
    <React.Fragment>
      <TableRow
        sx={{ "& > *": { borderBottom: "unset" } }}
        onClick={() => setOpen(!open)}
        className="fm-l2-parent-table-row"
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            className="p-0"
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          {moment(row.createdAt).format("M/D/YY - h:mm a")}
        </TableCell>

        <TableCell>{formatStage(row.stage)}</TableCell>

        <TableCell>{row.assigneeName || "-"}</TableCell>
        <TableCell>
          {row.firstName} {row.lastName}
        </TableCell>
        <TableCell>{row.phone}</TableCell>
        <TableCell>{row.email}</TableCell>

        {row.companyName !== "" && <TableCell>{row.companyName}</TableCell>}
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }} className="fm-l2-child-table-container lead-page-trow-expanded mt-10">
              <h6>Trip Details</h6>

              <p key="tripType">
                <strong>Trip Type: </strong>
                {row.tripType === 1
                  ? "One Way"
                  : row.tripType === 2
                  ? "Round Trip"
                  : "Multi-Leg"}
              </p>

              {!isMultileg ? (
                // Standard one-way or round trip display
                <>
                  <p key="departureDate">
                    <strong>Departure Date: </strong>
                    {row?.itineraryDetails?.departureDate || "-"}
                  </p>

                  {row.tripType === 2 && (
                    <p key="returnDate">
                      <strong>Return Date: </strong>
                      {row?.itineraryDetails?.returnDate || "N/A"}
                    </p>
                  )}

                  <p key="departureAirport">
                    <strong>Departure Airport: </strong>
                    {row?.itineraryDetails?.departureAirportName}
                  </p>

                  <p key="arrivalAirport">
                    <strong>Arrival Airport: </strong>
                    {row?.itineraryDetails?.arrivalAirportName}
                  </p>

                  <p key="departureTime">
                    <strong>Departure Time: </strong>
                    {row?.departureTime ? row?.departureTime : "N/A"}
                  </p>

                  {row.tripType === 2 && (
                    <p>
                      <strong>Return Time: </strong>
                      {row?.returnTime ? row?.returnTime : "N/A"}
                    </p>
                  )}
                </>
              ) : (
                // Multi-leg trip display
                <>
                  <p key="totalDistance">
                    <strong>Total Distance: </strong>
                    {row?.itineraryDetails?.distance?.toFixed(2)} NM
                  </p>

                  <p key="totalDateRange">
                    <strong>Date Range: </strong>
                    {row?.itineraryDetails?.totalDateRange} days
                  </p>

                  <p key="departureTime">
                    <strong>Departure Time: </strong>
                    {row?.departureTime ? row?.departureTime : "N/A"}
                  </p>

                  <div key="legs" style={{ marginTop: "15px" }}>
                    <strong>Legs:</strong>
                    {row?.itineraryDetails?.rawLegData?.map((leg, index) => (
                      <div
                        key={`leg-${index}`}
                        style={{
                          marginLeft: "20px",
                          marginTop: "10px",
                          borderLeft: "3px solid #ddd",
                          paddingLeft: "15px",
                        }}
                      >
                        <p style={{ margin: "5px 0" }}>
                          <strong>Leg {index + 1}:</strong>{" "}
                          {leg.departureAirport?.city} (
                          {leg.departureAirport?.localCode}) →{" "}
                          {leg.arrivalAirport?.city} (
                          {leg.arrivalAirport?.localCode})
                        </p>
                        <p style={{ margin: "5px 0", fontSize: "0.9em" }}>
                          Date:{" "}
                          {formatTripDate(
                            leg.departureDateString,
                            "MMM Do YYYY"
                          )}{" "}
                          | Passengers: {leg.passengers}
                        </p>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <hr></hr>

              <h6>Quote Details</h6>

              {row.quotes.length > 0 &&
                row.quotes.map((quote) => {
                  return (
                    <p key={`${quote}-p`}>
                      <Link key={quote} to={`/quotes/${quote}`}>
                        View Quote: {quote}
                      </Link>
                    </p>
                  );
                })}

              {!row.quotes.length && (
                <p>
                  <strong>No Quotes Sent.</strong>
                </p>
              )}

              <hr></hr>

              <h6>Additional Details</h6>

              {!isMultileg ? (
                // Standard one-way or round trip passenger count
                <p>
                  <strong>Passengers: </strong>
                  {row?.itineraryDetails?.passengerCount || "-"}
                </p>
              ) : (
                // Multi-leg passenger breakdown by leg
                <div style={{ marginBottom: "10px" }}>
                  <strong>Passengers by Leg:</strong>
                  {row?.itineraryDetails?.rawLegData?.map((leg, index) => (
                    <p
                      key={`passengers-leg-${index}`}
                      style={{ margin: "5px 0", marginLeft: "20px" }}
                    >
                      <strong>Leg {index + 1}:</strong> {leg.passengers}{" "}
                      passenger{leg.passengers !== 1 ? "s" : ""}
                    </p>
                  ))}
                </div>
              )}

              <p>
                <strong>Aircraft Selection: </strong>
                {getAircraftSelection(row)}
              </p>

              {row?.leadOrigin === "iosApp" ? (
                row?.iosBaggage?.length &&
                row.iosBaggage.some((item) => item.bagType && item.quantity) ? (
                  <div>
                    <strong>Baggage: </strong>
                    <ul>
                      {row.iosBaggage.map((item, index) =>
                        item.bagType && item.quantity ? (
                          <li key={index}>
                            {item.bagType}: {item.quantity}
                          </li>
                        ) : null
                      )}
                    </ul>
                  </div>
                ) : (
                  <p>
                    <strong>Baggage: </strong>N/A
                  </p>
                )
              ) : (
                <p>
                  <strong>Baggage: </strong>
                  {row?.baggage?.length ? row.baggage.join(", ") : "N/A"}
                </p>
              )}

              <p>
                <strong>Pets: </strong>
                {row.pets ? "Yes" : "No"}
              </p>
              <p>
                <strong>Infants: </strong>
                {row.infants ? "Yes" : "No"}
              </p>

              <p>
                <strong>Comments: </strong>
                {row.comments}
              </p>

              <hr></hr>

              <h6>Estimate Details</h6>

              {!isMultileg ? (
                // Standard trip options display
                row?.tripOptions?.length > 0 &&
                row?.tripOptions.map((option) => {
                  const {
                    aircraftName,
                    lowSpread,
                    highSpread,
                    finalPrice,
                  } = option;
                  const operatorPrice = getTripOptionOperatorPrice(
                    option,
                    row.tripType
                  );
                  return (
                    <div key={aircraftName} style={{ marginBottom: "14px" }}>
                      <p style={{ margin: "6px 0", textTransform: "uppercase" }}>
                        <strong>{aircraftName}</strong>
                      </p>
                      <p
                        style={{
                          marginTop: "6px",
                          marginBottom: "6px",
                          marginLeft: "15px",
                          fontSize: "1.05em",
                        }}
                      >
                        <strong>Estimate Range:</strong>{" "}
                        {currencyFormatter.format(lowSpread)} -{" "}
                        {currencyFormatter.format(highSpread)}
                      </p>
                      <p
                        style={{
                          marginTop: "6px",
                          marginBottom: "6px",
                          marginLeft: "15px",
                          fontSize: "1.05em",
                        }}
                      >
                        <strong>Calculator Price:</strong>{" "}
                        {finalPrice != null
                          ? currencyFormatter.format(finalPrice)
                          : "N/A"}
                      </p>
                      <p
                        style={{
                          marginTop: "6px",
                          marginBottom: "6px",
                          marginLeft: "15px",
                          fontSize: "1.05em",
                        }}
                      >
                        <strong>Expected Operator Price:</strong>{" "}
                        {operatorPrice != null
                          ? currencyFormatter.format(operatorPrice)
                          : "N/A"}
                      </p>
                    </div>
                  );
                })
              ) : // Multi-leg trip options display
              row?.tripOptions?.length > 0 ? (
                row.tripOptions.map(
                  (option, index) => {
                    return (
                      <div
                        key={`multileg-option-${index}`}
                        style={{
                          marginBottom: "15px",
                          padding: "10px",
                          backgroundColor: "#f5f5f5",
                          borderRadius: "4px",
                        }}
                      >
                        <p style={{ margin: "5px 0" }}>
                          <strong>
                            {option.aircraftOptionLabel || option.name}:
                          </strong>
                        </p>
                        <p style={{ margin: "5px 0", marginLeft: "15px" }}>
                          {option.commonlyAvailableLabel &&
                            `${option.commonlyAvailableLabel} | `}
                          <strong>Price:</strong>{" "}
                          {currencyFormatter.format(option.finalPrice)}
                        </p>
                        <p
                          style={{
                            margin: "5px 0",
                            marginLeft: "15px",
                            fontSize: "0.9em",
                          }}
                        >
                          Range: {currencyFormatter.format(option.lowSpread)} -{" "}
                          {currencyFormatter.format(option.highSpread)}
                          {option.fuelStops !== undefined &&
                            ` | Fuel Stops: ${option.fuelStops}`}
                        </p>
                      </div>
                    );
                  }
                )
              ) : (
                <p>No estimate data available.</p>
              )}

              <hr></hr>

              <h6>Lead Metadata</h6>

              <p>
                <strong>Lead ID: </strong>
                {row._id}
              </p>

              <p>
                <strong>Organization: </strong>
                {row.organizationName}
              </p>

              {(row.utmSource ||
                row.utmMedium ||
                row.utmCampaign ||
                row.utmTerm ||
                row.utmContent) && (
                <>
                  <p>
                    <strong>UTM Source: </strong>
                    {row.utmSource || "-"}
                  </p>

                  <p>
                    <strong>UTM Medium: </strong>
                    {row.utmMedium || "-"}
                  </p>

                  <p>
                    <strong>UTM Campaign: </strong>
                    {row.utmCampaign || "-"}
                  </p>

                  <p>
                    <strong>UTM Term: </strong>
                    {row.utmTerm || "-"}
                  </p>

                  <p>
                    <strong>UTM Content: </strong>
                    {row.utmContent || "-"}
                  </p>
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

function SourcingRow(props) {
  const { superAdminOrAd } = props;
  let { row, dispatch, selectedRows, handleDelete, handleHide } = props;
  const [open, setOpen] = React.useState(false);

  if (!row) return null;

  let rowSelected = selectedRows.filter(
    (selectedRow) => row._id === selectedRow._id
  );

  rowSelected = rowSelected?.length ? true : false;

  let matchingOperator = null;

  if (props?.leadData?.operatorData?.length) {
    matchingOperator = props?.leadData?.operatorData.filter(
      (item) => item._id === row.operator.id
    );

    if (matchingOperator?.length) {
      matchingOperator = matchingOperator[0];
    } else {
      matchingOperator = null;
    }
  }

  return (
    <>
      <TableRow
        sx={{ "& > *": { borderBottom: "unset" } }}
        onClick={() => setOpen(!open)}
        className={`fm-l2-parent-table-row ${
          row?.hidden ? "sourcing-record-hidden" : "sourcing-record-active"
        } `}
        key={row._id}
      >
        <TableCell key="toggleOpenClose">
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            className="p-0"
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        <TableCell className="centered-important p-0">
          <IconButton
            aria-label="expand row"
            size="small"
            disabled={row?.hidden}
            className="sourcing-record-checkbox"
            onClick={(e) => {
              e.stopPropagation();
              dispatch({
                type: "rowSelectionEvent",
                rowId: row._id,
                selected: !rowSelected,
              });
            }}
          >
            {rowSelected ? <CheckBoxIcon /> : <CheckBoxOutlineBlank />}
          </IconButton>
        </TableCell>

        <TableCell key="operatorName">{row?.operator?.name}</TableCell>

        <TableCell key="operatorPrice">
          {row.operatorPrice
            ? currencyFormatter.format(row?.operatorPrice)
            : "-"}
        </TableCell>

        <TableCell key="totalClientPayment">
          {row.totalClientPayment
            ? currencyFormatter.format(row.totalClientPayment)
            : "-"}
        </TableCell>

        <TableCell key="aircraftName">{row?.aircraft?.name || "-"}</TableCell>

        <TableCell key="createdAt">
          {moment(row.createdAt).format("M/D/YY - h:mm a")}
        </TableCell>

        <TableCell key="quoteStatus">
          {row?.latestQuoteId && row?.latestQuoteTimeStamp ? (
            <Link to={`/quotes/${row?.latestQuoteId}`}>
              {moment(row?.latestQuoteTimeStamp).format("lll")}
            </Link>
          ) : row?.latestQuoteId ? (
            <Link to={`/quotes/${row?.latestQuoteId}`}>View Draft</Link>
          ) : (
            "-"
          )}
        </TableCell>

        <TableCell key="record-actions">
          <div className="d-flex justify-content-end">
            {!row?.hidden && (
              <>
                <Link
                  className="d-block base-button icon-button icon-button-small evo-blue ml-2"
                  title="Edit Sourcing"
                  to={{
                    pathname: `/sourcing/${row?._id}`,
                    state: {
                      lead: props.leadData,
                    },
                  }}
                >
                  <span className="material-icons">edit</span>
                </Link>
                <button
                  key={`handle-delete-${row?._id}`}
                  onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(row._id);
                    }
                  }
                  className="d-block base-button icon-button icon-button-small btn-danger ml-2"
                  title="Delete Sourcing"
                >
                  <span className="material-icons">delete</span>
                </button>
              </>)}
            <button
              key={`handle-hide-${row?._id}`}
              onClick={(e) => {
                e.stopPropagation();
                handleHide(row._id);
              }
              }
              className="d-block base-button icon-button icon-button-small evo-blue ml-2"
              title={row?.hidden ? "Unhide sourcing" : "Hide sourcing"}
            >
              {row?.hidden ?
                <span className="material-icons">visibility</span>
                : <span className="material-icons">visibility_off</span>
              }
            </button>
            {!row?.hidden && props.superAdmin && (
              <Link
                className="d-block base-button icon-button icon-button-small evo-blue ml-2"
                title="New Contract"
                to={{
                  pathname: `/contracts/new`,
                  state: {
                    lead: props.leadData,
                    sourcingRecords: [row],
                  },
                }}
              >
                <span className="material-icons">description</span>
              </Link>
            )}
          </div>
        </TableCell>
      </TableRow>
      <TableRow
        className={`fm-l2-parent-table-row ${
          row?.hidden ? "sourcing-record-hidden" : "sourcing-record-active"
        }`}
      >
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }} className="fm-l2-child-table-container lead-page-trow-expanded mt-10">
              <div className="three-columns">
                <div className="column">
                  <h6>Aircraft Details</h6>

                  <p>
                    <strong>Tail #: </strong>
                    {row.tailNumber || "-"}
                  </p>

                  <p key={`seats`}>
                    <strong>Seats: </strong>
                    {row?.aircraft?.seats ? row?.aircraft?.seats : "-"}
                  </p>

                  <p key={`class`}>
                    <strong>Class: </strong>
                    {row?.aircraft?.aircraftType?.name
                      ? row?.aircraft?.aircraftType?.name
                      : "-"}
                  </p>
                  <p key={`yom`}>
                    <strong>YOM: </strong>
                    {row?.yomIsRange && row?.yom && row?.yomEnd
                      ? `${row.yom} - ${row.yomEnd}`
                      : row?.yom
                      ? row.yom
                      : row?.fullAircraftData?.yom
                      ? row?.fullAircraftData?.yom
                      : "-"}
                  </p>

                  <p key={`interiorRefurb`}>
                    <strong>Interior Refurb: </strong>
                    {row?.interiorRefurbYearIsRange &&
                    row?.interiorRefurbYear &&
                    row?.interiorRefurbYearEnd
                      ? `${row.interiorRefurbYear} - ${row.interiorRefurbYearEnd}`
                      : row?.interiorRefurbYear
                      ? row?.interiorRefurbYear
                      : "-"}
                  </p>

                  <p key={`ownerApproval`}>
                    <strong>Owner Approval Required: </strong>
                    {row.ownerApproval ? "Yes" : "No"}
                  </p>

                  <p key={`amenities`}>
                    <strong>Amenities: </strong>
                    {row?.amenities?.length ? row?.amenities?.join(", ") : "-"}
                  </p>

                  <p key={`safetyStandards`}>
                    <strong>Safety Standards: </strong>
                    {row?.safetyStandards?.length
                      ? row?.safetyStandards?.join(", ")
                      : "-"}
                  </p>
                </div>

                <div className="column">
                  <h6>Payment Details</h6>

                  <p key={`totalClientPayment`}>
                    <strong>Total Client Payment: </strong>
                    {row?.totalClientPayment
                      ? currencyFormatter.format(row?.totalClientPayment)
                      : "-"}
                  </p>

                  <p key={`operatorPrice`}>
                    <strong>Operator Price: </strong>
                    {row?.operatorPrice
                      ? currencyFormatter.format(row.operatorPrice)
                      : "-"}
                  </p>

                  <p key={`fet`}>
                    <strong>FET: </strong>
                    {row?.fet?.amount
                      ? currencyFormatter.format(row?.fet?.amount)
                      : "-"}
                  </p>
                  <p key={`seg-fees`}>
                    <strong>Segment Fees: </strong>
                    {row?.segmentFees?.amount
                      ? currencyFormatter.format(row?.segmentFees?.amount)
                      : "-"}
                  </p>

                  <p key={`referral-fees`}>
                    <strong>Referral Fees: </strong>
                    {row?.referralFees
                      ? currencyFormatter.format(row?.referralFees)
                      : "-"}
                  </p>

                  <p key={`ccFees`}>
                    <strong>CC Fee: </strong>
                    {row?.paymentType === "cc" && row?.ccFee
                      ? currencyFormatter.format(row?.ccFee)
                      : "-"}
                  </p>

                  <p key={`net-commission`}>
                    <strong>Net Commission: </strong>
                    {row?.netCommission
                      ? currencyFormatter.format(row.netCommission)
                      : "-"}
                  </p>

                  <p key={`gross-commission`}>
                    <strong>Gross Commission: </strong>
                    {row?.grossCommission
                      ? currencyFormatter.format(row.grossCommission)
                      : "-"}
                  </p>
                </div>

                <div className="column">
                  <h6>Metadata</h6>

                  <p key={`flightTime`}>
                    <strong>Flight Time: </strong>
                    {!row?.isRoundTrip && row?.flightTime
                      ? timeFormatter(row.flightTime)
                      : row?.isRoundTrip &&
                        row?.originLegFlightTime &&
                        row?.returnLegFlightTime
                      ? `${timeFormatter(
                          row.originLegFlightTime
                        )}, ${timeFormatter(row.returnLegFlightTime)}`
                      : "-"}
                  </p>

                  <p key={`costPerFlightHour`}>
                    <strong>Operator Hourly: </strong>
                    {row?.costPerFlightHour
                      ? currencyFormatter.format(row.costPerFlightHour)
                      : "-"}
                  </p>

                  <p key={`evoCostPerFlightHour`}>
                    <strong>Evo Hourly: </strong>
                    {row?.evoCostPerFlightHour
                      ? currencyFormatter.format(row.evoCostPerFlightHour)
                      : "-"}
                  </p>

                  <p key={`notes`}>
                    <strong>Notes: </strong>
                    {row?.notes || "-"}
                  </p>

                  <p key={`includeNotesOnQuote`}>
                    <strong>Include Notes On Quote: </strong>
                    {row?.includeNotesOnQuote ? "Yes" : "No"}
                  </p>

                  <p key={`includeOPIOnQuote`}>
                    <strong>Include OPI On Quote: </strong>
                    {row?.includeOPIOnQuote ? "Yes" : "No"}
                  </p>

                  <p key={`opi-score`}>
                    <strong>OPI: </strong>
                    {matchingOperator?.composedScore
                      ? matchingOperator.composedScore
                      : "-"}
                  </p>
                </div>
              </div>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export function SortableTableHead(props) {
  let showSortIcon = true;
  let sortUpText = "arrow_drop_up";
  let sortDownText = "arrow_drop_down";

  let isSorted = props.sortField === props.id ? true : false;

  return (
    <TableCell
      onClick={(e) => {
        props.onClick(props.id);
      }}
      id={props.id}
      className="sortable-evo-header"
    >
      <div className="sortable-evo-header-container">
        {props.label}{" "}
        {isSorted && (
          <span
            className={`material-icons sort-icon ${
              showSortIcon ? "sort-icon-visible" : "sort-icon-hidden"
            }`}
          >
          {props.sortDirection === 1 ? sortUpText : sortDownText}
        </span>
        )}
      </div>
    </TableCell>
  );
}

export default function CollapsibleTable(props) {
  const { superAdmin } = props;

  return (
    <>
      <h5 className="mt-30 mb-1">Lead Data</h5>

      <TableContainer
        component={Paper}
        className="accordion-table-with-details shadow-none"
        key="leadDataTable"
      >
        <Table aria-label="collapsible table" className="table-compact-view">
          <TableHead className="fm-l2-parent-table-head">
            <TableRow className="fm-l2-parent-table-head-row">
              <TableCell />
              <TableCell>DATE REQUESTED</TableCell>
              <TableCell>STAGE</TableCell>
              <TableCell>ASSIGNEE</TableCell>
              <TableCell>LEAD NAME</TableCell>
              <TableCell>PHONE NUMBER</TableCell>
              <TableCell>EMAIL</TableCell>

              {props.rows[0]?.companyName !== "" && (
                <TableCell>COMPANY</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody className="fm-l2-parent-table-body">
            {props.rows.map((row) => (
              <Row key={row._id} row={row} superAdminOrAd={props.superAdmin} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <>
        <h5 className="mt-30 mb-1 d-flex">
          Sourcing

          <Link
            className="base-button icon-button icon-button-small evo-dark-green ml-2"
            to={{pathname: `/sourcing/new`, state: {lead: props.lead}}}
            title="Add Record"
          >
            <span className="material-icons">add_circle</span>
          </Link>

          {props?.selectedRows?.length > 0 && (
            <Link
              className="base-button icon-button icon-button-small evo-dark-green ml-2"
              to={{
                pathname: `/leads/${props?.lead?._id}/quotes/new`,
                state: {
                  sourcingRecords: props.selectedRows,
                  leadL2Data: props.lead,
                },
              }} title="Create Quote"
            >
              <span className="material-icons">request_quote</span>
            </Link>)}
        </h5>

        <TableContainer
          component={Paper}
          className="mt-0 accordion-table-with-details shadow-none"
          key="sourcingTable"
        >
          <Table aria-label="collapsible table" className="table-compact-view">
            <TableHead className="fm-l2-parent-table-head">
              <TableRow className="fm-l2-parent-table-head-row">
                <TableCell/>
                <TableCell>SELECT</TableCell>

                <SortableTableHead
                  onClick={props.handleSourcingSort}
                  id="operatorName"
                  label="OPERATOR"
                  sortDirection={props.sourcingSortDirection}
                  sortField={props.sourcingSortField}
                />

                <SortableTableHead
                  onClick={props.handleSourcingSort}
                  id="operatorPrice"
                  label="OPERATOR PRICE"
                  sortDirection={props.sourcingSortDirection}
                  sortField={props.sourcingSortField}
                />

                <SortableTableHead
                  onClick={props.handleSourcingSort}
                  id="totalClientPayment"
                  label="TOTAL PRICE"
                  sortDirection={props.sourcingSortDirection}
                  sortField={props.sourcingSortField}
                />

                <SortableTableHead
                  onClick={props.handleSourcingSort}
                  id="aircraftName"
                  label="AIRCRAFT"
                  sortDirection={props.sourcingSortDirection}
                  sortField={props.sourcingSortField}
                />

                <SortableTableHead
                  onClick={props.handleSourcingSort}
                  id="createdAt"
                  label="CREATED AT"
                  sortDirection={props.sourcingSortDirection}
                  sortField={props.sourcingSortField}
                />

                <SortableTableHead
                  onClick={props.handleSourcingSort}
                  id="quoteStatus"
                  label="QUOTE STATUS"
                  sortDirection={props.sourcingSortDirection}
                  sortField={props.sourcingSortField}
                />

                <TableCell>
                  <div className="d-flex justify-content-end">ACTIONS</div>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody className="fm-l2-parent-table-body">
              {props?.sourcingRows?.length > 0 ? props?.sourcingRows.map((row) => (
                  <SourcingRow
                    key={row?._id}
                    row={row}
                    leadData={props.lead}
                    handleDelete={props.handleSourcingRecordDelete}
                    handleHide={props.handleSourcingRecordHide}
                    dispatch={props.parentDispatch}
                    selectedRows={props.selectedRows}
                    superAdmin={superAdmin}
                  />)) :
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography className="mt-10 mb-10">There are no records to display</Typography>
                  </TableCell>
                </TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    </>
  );
}
